import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Cpu, 
  Sparkles, 
  Shield, 
  Zap, 
  Target, 
  Check, 
  X, 
  Wallet,
  Loader2,
  ArrowRight,
  Copy,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

interface NFTMintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (agentData: any) => void;
}

const strategies = [
  { 
    id: 'aggressive', 
    name: 'Berserker', 
    icon: Zap, 
    color: 'text-red-500', 
    bg: 'bg-red-500/10', 
    border: 'border-red-500/30',
    desc: 'High aggression, maximum damage output',
    stats: { aggression: 90, defense: 20, mobility: 70, utility: 30 }
  },
  { 
    id: 'balanced', 
    name: 'Tactician', 
    icon: Target, 
    color: 'text-gold', 
    bg: 'bg-gold/10', 
    border: 'border-gold/30',
    desc: 'Adaptive tactics, well-rounded performance',
    stats: { aggression: 50, defense: 50, mobility: 60, utility: 60 }
  },
  { 
    id: 'defensive', 
    name: 'Fortress', 
    icon: Shield, 
    color: 'text-blue-500', 
    bg: 'bg-blue-500/10', 
    border: 'border-blue-500/30',
    desc: 'High survivability, strategic positioning',
    stats: { aggression: 30, defense: 90, mobility: 40, utility: 50 }
  },
];

const MINT_STEPS = [
  { id: 'approve', label: 'Approve Token', desc: 'Approve $MON spending' },
  { id: 'mint', label: 'Mint NFT', desc: 'Create your AI Agent' },
  { id: 'confirm', label: 'Confirm', desc: 'Wait for blockchain confirmation' },
];

export default function NFTMintModal({ isOpen, onClose, onSuccess }: NFTMintModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<'form' | 'approving' | 'minting' | 'confirming' | 'success'>('form');
  const [agentName, setAgentName] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState('balanced');
  const [currentStep, setCurrentStep] = useState(0);
  const [txHash, setTxHash] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setAgentName('');
      setSelectedStrategy('balanced');
      setCurrentStep(0);
      setTxHash('');
    }
  }, [isOpen]);

  const handleMint = async () => {
    if (!agentName.trim()) return;
    
    setStep('approving');
    setCurrentStep(0);
    
    // Simulate approval
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setStep('minting');
    setCurrentStep(1);
    
    // Simulate minting
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    setStep('confirming');
    setCurrentStep(2);
    
    // Simulate confirmation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Generate fake tx hash
    const fakeHash = '0x' + Array(64).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
    setTxHash(fakeHash);
    
    const agentData = {
      id: fakeHash.slice(0, 16),
      name: agentName,
      strategy: selectedStrategy,
      strategyName: strategies.find(s => s.id === selectedStrategy)?.name,
      createdAt: new Date().toISOString().split('T')[0],
      battles: 0,
      wins: 0,
      kills: 0,
      top3: 0,
      config: strategies.find(s => s.id === selectedStrategy)?.stats,
    };
    
    localStorage.setItem('rumbleclaw_agent', JSON.stringify(agentData));
    setStep('success');
    onSuccess(agentData);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-bg/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-[600px] bg-surface border border-line rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
              <Sparkles size={20} className="text-gold" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-ink">Mint AI Agent NFT</h2>
              <p className="text-xs text-muted">Create your unique battle companion</p>
            </div>
          </div>
          {step === 'form' && (
            <button onClick={onClose} className="text-muted hover:text-ink transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'form' && (
            <div className="space-y-6">
              {/* Agent Name */}
              <div>
                <label className="text-sm font-semibold text-ink mb-2 block">
                  Agent Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Enter a unique name for your agent..."
                  className="w-full px-4 py-3 bg-bg border border-line rounded-xl text-ink placeholder:text-muted focus:outline-none focus:border-gold/50 transition-colors"
                  maxLength={20}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted">Max 20 characters</span>
                  <span className="text-xs text-muted">{agentName.length}/20</span>
                </div>
              </div>

              {/* Strategy Selection */}
              <div>
                <label className="text-sm font-semibold text-ink mb-3 block">
                  Battle Strategy <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {strategies.map((strategy) => (
                    <button
                      key={strategy.id}
                      onClick={() => setSelectedStrategy(strategy.id)}
                      className={cn(
                        "w-full p-4 border rounded-xl text-left transition-all flex items-center gap-4",
                        selectedStrategy === strategy.id
                          ? cn("bg-gold/5", strategy.border)
                          : "border-line hover:border-gold/30"
                      )}
                    >
                      <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center", strategy.bg)}>
                        <strategy.icon size={28} className={strategy.color} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-ink">{strategy.name}</span>
                          {selectedStrategy === strategy.id && (
                            <span className="px-2 py-0.5 bg-gold/20 text-gold text-[10px] rounded-full">Selected</span>
                          )}
                        </div>
                        <p className="text-sm text-muted mt-0.5">{strategy.desc}</p>
                        
                        {/* Mini Stats */}
                        <div className="flex gap-3 mt-2">
                          {Object.entries(strategy.stats).slice(0, 2).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-1">
                              <div className="w-16 h-1.5 bg-bg rounded-full overflow-hidden">
                                <div 
                                  className={cn("h-full rounded-full", strategy.color.replace('text-', 'bg-'))} 
                                  style={{ width: `${val}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-muted uppercase">{key.slice(0, 3)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mint Info */}
              <div className="p-4 bg-bg border border-line rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-gold mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-ink font-medium mb-1">Mint Cost: 100 $MON</p>
                    <p className="text-muted">Your agent will be minted as an NFT on the Monad blockchain. You can only have one agent per wallet.</p>
                  </div>
                </div>
              </div>

              {/* Mint Button */}
              <button
                onClick={handleMint}
                disabled={!agentName.trim()}
                className={cn(
                  "w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2",
                  agentName.trim()
                    ? "bg-gold text-bg hover:bg-white"
                    : "bg-muted text-muted cursor-not-allowed"
                )}
              >
                <Sparkles size={20} />
                Mint Agent NFT (100 $MON)
              </button>
            </div>
          )}

          {(step === 'approving' || step === 'minting' || step === 'confirming') && (
            <div className="py-8 text-center">
              {/* Progress Steps */}
              <div className="flex items-center justify-center gap-2 mb-8">
                {MINT_STEPS.map((s, idx) => (
                  <React.Fragment key={s.id}>
                    <div className={cn(
                      "flex flex-col items-center",
                      idx <= currentStep ? "opacity-100" : "opacity-40"
                    )}>
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all",
                        idx < currentStep ? "bg-green-500 text-bg" :
                        idx === currentStep ? "bg-gold text-bg animate-pulse" :
                        "bg-surface border border-line"
                      )}>
                        {idx < currentStep ? (
                          <Check size={20} />
                        ) : idx === currentStep ? (
                          <Loader2 size={20} className="animate-spin" />
                        ) : (
                          <span className="text-sm">{idx + 1}</span>
                        )}
                      </div>
                      <span className="text-xs font-medium">{s.label}</span>
                    </div>
                    {idx < MINT_STEPS.length - 1 && (
                      <div className={cn(
                        "w-12 h-0.5 mb-6",
                        idx < currentStep ? "bg-green-500" : "bg-line"
                      )} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Current Action */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-ink mb-2">
                  {step === 'approving' && 'Approving $MON...'}
                  {step === 'minting' && 'Minting NFT...'}
                  {step === 'confirming' && 'Waiting for Confirmation...'}
                </h3>
                <p className="text-muted">
                  {step === 'approving' && 'Please approve the token spending in your wallet'}
                  {step === 'minting' && 'Creating your unique AI Agent on the blockchain'}
                  {step === 'confirming' && 'Transaction submitted, waiting for block confirmation'}
                </p>
              </div>

              {/* Simulated Progress */}
              <div className="max-w-[300px] mx-auto">
                <div className="h-2 bg-bg rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gold rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: step === 'approving' ? '33%' : step === 'minting' ? '66%' : '90%' }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              <p className="text-xs text-muted mt-4">
                Do not close this window until the transaction is complete
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-6 text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center"
              >
                <Check size={40} className="text-green-500" />
              </motion.div>

              <h3 className="text-2xl font-bold text-ink mb-2">NFT Minted Successfully!</h3>
              <p className="text-muted mb-6">
                Your AI Agent <span className="text-gold font-semibold">{agentName}</span> is now on the blockchain
              </p>

              {/* NFT Preview Card */}
              <div className="max-w-[280px] mx-auto mb-6">
                <div className="bg-gradient-to-br from-gold/20 to-purple-500/20 rounded-2xl p-6 border border-gold/30">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-xl bg-surface flex items-center justify-center">
                    <Cpu size={48} className="text-gold" />
                  </div>
                  <h4 className="text-lg font-bold text-ink mb-1">{agentName}</h4>
                  <p className="text-sm text-muted mb-3">
                    {strategies.find(s => s.id === selectedStrategy)?.name}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-xs text-muted">
                    <span className="px-2 py-1 bg-surface rounded">NFT</span>
                    <span className="px-2 py-1 bg-surface rounded">ERC-721</span>
                  </div>
                </div>
              </div>

              {/* Transaction Hash */}
              {txHash && (
                <div className="mb-6">
                  <div className="text-xs text-muted mb-1">Transaction Hash</div>
                  <div className="flex items-center justify-center gap-2">
                    <code className="px-3 py-2 bg-bg rounded-lg text-sm text-muted font-mono">
                      {txHash.slice(0, 20)}...{txHash.slice(-8)}
                    </code>
                    <button 
                      onClick={() => handleCopy(txHash)}
                      className="p-2 hover:bg-surface rounded-lg transition-colors"
                    >
                      {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-muted" />}
                    </button>
                    <a 
                      href={`https://explorer.monad.xyz/tx/${txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-surface rounded-lg transition-colors"
                    >
                      <ExternalLink size={16} className="text-muted" />
                    </a>
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="px-8 py-3 bg-gold text-bg rounded-xl font-semibold hover:bg-white transition-colors"
              >
                View My Agent
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
